<template>
  <div class="operacao-central-page">
    <CentralOperationsHeader />

    <b-card
      v-if="facebookAccountsLista.length > 1"
      class="co-store-card mb-4"
    >
      <div class="d-flex flex-wrap align-items-center">
        <feather-icon
          icon="StoreIcon"
          size="18"
          class="mr-2 text-primary"
        />
        <label class="mb-0 mr-3 font-weight-bold">Selecionar Loja:</label>
        <b-form-select
          v-model="chatSelecionado"
          :options="facebookAccountsLista.map(conta => ({
            value: conta.chat_token,
            text: conta.name
          }))"
          class="co-store-select"
          @change="onStoreChange"
        />
      </div>
    </b-card>

    <div
      v-if="carregando && !atendimentosAtuais.length && !resolvidosPeriodo.length"
      class="text-center py-5"
    >
      <b-spinner type="grow" />
      <p class="text-muted mt-2 mb-0">
        Carregando operação da central…
      </p>
    </div>

    <template v-else>
      <CentralRealtimeSection
        class="mb-4"
        :row1="realtimeRow1"
        :bot-headline="botHeadline"
        :bot-bullets="botBullets"
        :bot-highlighted="botHighlighted"
        :avg-wait="avgWaitFormatted"
        :narrative-realtime="narrativeRealtime"
      />

      <CentralPeriodSection
        class="mb-4"
        :period-preset="periodPreset"
        :period-trigger-label="periodTriggerLabel"
        :period-range-text="periodRangeText"
        :compare-text="compareText"
        :cards="periodCards"
        :narrative-period="narrativePeriod"
        :loading="carregandoPeriodo"
        :periodo-inicio="periodoInicio"
        :periodo-fim="periodoFim"
        @period-preset="setPeriodPreset"
        @apply-custom="aplicarPeriodoCustom"
      />

      <CentralBotVsHumanSection
        class="mb-4"
        :categories="chartCategories"
        :series-total="chartTotal"
        :series-human="chartHuman"
        :series-bot="chartBot"
      />

      <CentralHeatmapAtendimentosSection
        class="mb-4"
        :atendimentos="resolvidosPeriodoFimBr"
        :loading="carregandoPeriodo"
      />

      <CentralBottlenecksSection
        class="mb-4"
        :summary-items="bottleneckSummary"
      />

      <section class="co-surface co-historico">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h2 class="co-section-title co-historico__title mb-1">
              Histórico de atendimentos
            </h2>
            <p class="co-historico__desc mb-0">
              Atendimentos em andamento (tempo real) e finalizados cujo encerramento cai no período (calendário Brasil). Clique na linha para ver o histórico de mensagens.
            </p>
          </div>
          <b-badge
            variant="secondary"
            class="co-historico__badge"
          >
            {{ periodRangeText }}
          </b-badge>
        </div>

        <b-card class="co-historico__filtros mb-3">
          <div class="d-flex align-items-end">
            <b-form-group
              label="Digite o nome do cliente ou WhatsApp"
              label-for="oc-filtro-cliente"
              class="mb-0 mr-2"
            >
              <b-form-input
                id="oc-filtro-cliente"
                v-model="filtroHistorico.cliente"
                size="sm"
                style="min-width: 160px"
              />
            </b-form-group>
            <b-form-group
              label="Status"
              label-for="oc-filtro-status"
              class="mb-0 mr-2"
            >
              <b-form-select
                id="oc-filtro-status"
                v-model="filtroHistorico.status"
                :options="opcoesStatusHistorico"
                size="sm"
                style="min-width: 160px"
              />
            </b-form-group>
            <b-form-group
              label="Atendente / Bot"
              label-for="oc-filtro-atendente"
              class="mb-0 mr-2"
            >
              <b-form-select
                id="oc-filtro-atendente"
                v-model="filtroHistorico.atendente"
                :options="opcoesAtendenteHistorico"
                size="sm"
                style="min-width: 180px"
              />
            </b-form-group>
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="limparFiltrosHistorico"
            >
              Limpar filtros
            </b-button>
          </div>
        </b-card>

        <div
          v-if="carregandoPeriodo"
          class="text-center py-3 text-muted"
        >
          <b-spinner
            small
            class="mr-2"
          />
          Atualizando lista do período…
        </div>
        <b-table
          v-else
          :items="listaHistoricoPagina"
          :fields="camposTabelaHistorico"
          striped
          hover
          responsive
          small
          bordered
          class="co-historico__table"
          show-empty
          empty-text="Nenhum atendimento encontrado."
          @row-clicked="abrirHistoricoAtendimento"
        >
          <template #cell(cliente)="data">
            {{ data.item.cliente || data.item.clienteNome || '—' }}
          </template>
          <template #cell(whatsapp)="data">
            {{ data.item.whatsapp || data.item.clienteWhatsapp || '—' }}
          </template>
          <template #cell(atendente)="data">
            {{ data.item.atendenteNome || (data.item.atendimento_bot ? 'Bot/IA' : '—') }}
          </template>
          <template #cell(status)="data">
            <b-badge :variant="badgeStatusHistorico(data.item.statusConversa)">
              {{ labelStatusHistorico(data.item.statusConversa) }}
            </b-badge>
          </template>
          <template #cell(inicio)="data">
            {{ formatarDataHoraHistorico(data.item.inicio) }}
          </template>
          <template #cell(fim)="data">
            {{ data.item.fim ? formatarDataHoraHistorico(data.item.fim) : '—' }}
          </template>
        </b-table>
        <b-pagination
          v-if="listaHistoricoFiltrada.length > porPaginaHistorico"
          v-model="paginaHistorico"
          :total-rows="listaHistoricoFiltrada.length"
          :per-page="porPaginaHistorico"
          size="sm"
          align="center"
          class="mt-3 mb-0"
        />

        <ModalHistoricoAtendimento
          :visivel.sync="modalHistoricoVisivel"
          :historico="historicoAtendimentoSelecionado"
          :atendentes="listaAtendentesParaModal"
        />
      </section>
    </template>
  </div>
</template>

<script>
import {
  getAttendings,
  buscaResolvidos,
  buscaAtendentes,
  buscaChatsTeste,
  getChatOfContact,
} from '@/functions/basics/chat'
import ModalHistoricoAtendimento from '../ModalHistoricoAtendimento.vue'
import CentralOperationsHeader from './CentralOperationsHeader.vue'
import CentralRealtimeSection from './CentralRealtimeSection.vue'
import CentralPeriodSection from './CentralPeriodSection.vue'
import CentralBotVsHumanSection from './CentralBotVsHumanSection.vue'
import CentralBottlenecksSection from './CentralBottlenecksSection.vue'
import CentralHeatmapAtendimentosSection from './CentralHeatmapAtendimentosSection.vue'
import {
  brazilTodayISO,
  addDaysBrazilISO,
  daysBetweenISOBr,
  isoDatePickerToBrazilISO,
  isoBrToLocalDate,
  tempoResolucaoMinutos,
  average,
  formatMinutosLegivel,
  formatMsLegivel,
  deltaPercentual,
  seriePorHoraInicio,
  faixaCriticaPorPicos,
  countIniciadasPorAtendenteNoIntervalo,
  countComInicioNoIntervalo,
  countAbertosComInicioNoIntervalo,
  filterResolvidosFimNaFaixaBr,
  inicioNaFaixaBr,
  iniciadaPorAtendente,
  resolvidoPorAtendenteHumano,
  resolvidoPorBot,
  contagemFinalizadosBotNaFaixaFimBr,
  contagemFinalizadosHumanoNaFaixaFimBr,
  TIMEZONE_BR,
} from './operacao-central-utils'

const STATUS_NAO_ATENDIDA = 'nao_atendida'
const STATUS_EM_ANDAMENTO = 'em_andamento'
const STATUS_RESOLVIDO = 'resolvido'

export default {
  name: 'OperacaoCentral',
  components: {
    ModalHistoricoAtendimento,
    CentralOperationsHeader,
    CentralRealtimeSection,
    CentralPeriodSection,
    CentralBotVsHumanSection,
    CentralBottlenecksSection,
    CentralHeatmapAtendimentosSection,
  },
  data() {
    const fimBr = brazilTodayISO()
    const inicioBr = addDaysBrazilISO(fimBr, -6)
    return {
      facebookAccountsLista: [],
      chatSelecionado: null,
      carregando: false,
      carregandoPeriodo: false,
      periodPreset: '7',
      periodoInicioISOBr: inicioBr,
      periodoFimISOBr: fimBr,
      atendimentosAtuais: [],
      resolvidosHoje: [],
      resolvidosPeriodo: [],
      resolvidosPeriodoAnterior: [],
      intervaloRefresh: null,
      mapaAtendentes: {},
      modalHistoricoVisivel: false,
      historicoAtendimentoSelecionado: null,
      filtroHistorico: {
        status: null,
        atendente: null,
        cliente: '',
      },
      paginaHistorico: 1,
      porPaginaHistorico: 10,
      camposTabelaHistorico: [
        { key: 'cliente', label: 'Cliente' },
        { key: 'whatsapp', label: 'WhatsApp' },
        { key: 'atendente', label: 'Atendente' },
        { key: 'status', label: 'Status' },
        { key: 'inicio', label: 'Início' },
        { key: 'fim', label: 'Fim' },
      ],
    }
  },
  computed: {
    listaAtendentesParaModal() {
      return Object.entries(this.mapaAtendentes || {}).map(([id, name]) => ({
        id: parseInt(id, 10),
        value: parseInt(id, 10),
        name,
        text: name,
      }))
    },
    periodoInicio() {
      return isoBrToLocalDate(this.periodoInicioISOBr)
    },
    periodoFim() {
      return isoBrToLocalDate(this.periodoFimISOBr)
    },
    hojeBrasilISO() {
      return brazilTodayISO()
    },
    /** Métricas da faixa narrativa do bloco tempo real: sempre o dia civil “hoje” (BR), independente do período histórico. */
    hojeTotalInicioNoPeriodo() {
      const hoje = this.hojeBrasilISO
      return countComInicioNoIntervalo(
        this.atendimentosAtuais,
        this.resolvidosHoje,
        hoje,
        hoje,
      )
    },
    hojeTotalResolvido() {
      return (this.resolvidosHoje || []).length
    },
    hojeTaxaResolucaoHumano() {
      const list = this.resolvidosHoje || []
      if (!list.length) return 0
      const human = list.filter(resolvidoPorAtendenteHumano).length
      return Math.round((100 * human) / list.length)
    },
    realtimeRow1() {
      const inProgress = this.atendimentosAtuais.length
      const unattended = this.atendimentosAtuais.filter(at => {
        if (at.atendimento_bot) return false
        return !(at.atendente_ativo && at.atendente_ativo.id)
      }).length
      const humanInProgress = this.atendimentosAtuais.filter(at => {
        if (at.atendimento_bot) return false
        return !!(at.atendente_ativo && at.atendente_ativo.id)
      }).length
      const botInProgress = this.atendimentosAtuais.filter(at => at.atendimento_bot === true).length
      const hoje = this.hojeBrasilISO
      const iniciadasHojeAtt = this.atendimentosAtuais.filter(
        at => iniciadaPorAtendente(at) && inicioNaFaixaBr(at, hoje, hoje),
      ).length
      const finHumanoHoje = this.resolvidosHoje.filter(resolvidoPorAtendenteHumano).length
      const finBotHoje = this.resolvidosHoje.filter(resolvidoPorBot).length
      return [
        { key: 'p1', value: inProgress, label: 'Atendimentos em andamento' },
        { key: 'p2', value: unattended, label: 'Não atendidos / fila' },
        { key: 'p3', value: humanInProgress, label: 'Em atendimento humano' },
        { key: 'p4', value: botInProgress, label: 'Em atendimento pelo bot' },
        { key: 'p5', value: iniciadasHojeAtt, label: 'Iniciadas por atendentes (hoje)' },
        { key: 'p6', value: finHumanoHoje, label: 'Finalizados hoje (atendente)' },
        { key: 'p7', value: finBotHoje, label: 'Finalizados hoje (bot)' },
      ]
    },
    avgWaitMs() {
      const na = this.atendimentosAtuais.filter(at => {
        if (at.atendimento_bot) return false
        return !(at.atendente_ativo && at.atendente_ativo.id)
      })
      if (!na.length) return null
      const now = Date.now()
      const vals = na.map(at => {
        const t = at.inicio || at.created_at
        if (!t) return null
        return now - new Date(t).getTime()
      }).filter(v => v != null && v >= 0)
      if (!vals.length) return null
      return vals.reduce((a, b) => a + b, 0) / vals.length
    },
    avgWaitFormatted() {
      return formatMsLegivel(this.avgWaitMs)
    },
    botShareActive() {
      const human = this.atendimentosAtuais.filter(at => !at.atendimento_bot && (at.atendente_ativo && at.atendente_ativo.id)).length
      const bot = this.atendimentosAtuais.filter(at => at.atendimento_bot === true).length
      const t = human + bot
      if (!t) return 0
      return Math.round((100 * bot) / t)
    },
    botResolvedTodayPct() {
      if (!this.resolvidosHoje.length) return 0
      const b = this.resolvidosHoje.filter(at => at.atendimento_bot === true).length
      return Math.round((100 * b) / this.resolvidosHoje.length)
    },
    botVsHumanResolutionToday() {
      const hTimes = this.resolvidosHoje.filter(at => !at.atendimento_bot).map(tempoResolucaoMinutos).filter(v => v != null)
      const bTimes = this.resolvidosHoje.filter(at => at.atendimento_bot === true).map(tempoResolucaoMinutos).filter(v => v != null)
      return {
        human: average(hTimes),
        bot: average(bTimes),
      }
    },
    botHighlighted() {
      const { human, bot } = this.botVsHumanResolutionToday
      const shareOk = this.botShareActive >= 22
      const resolvedOk = this.botResolvedTodayPct >= 18
      const faster = bot != null && human != null && bot < human
      return (shareOk || resolvedOk) && (faster || resolvedOk || shareOk)
    },
    botHeadline() {
      return `Bot está com ${this.botShareActive}% dos atendimentos em andamento`
    },
    botBullets() {
      const { human, bot } = this.botVsHumanResolutionToday
      const lines = [
        `Bot resolveu ${this.botResolvedTodayPct}% dos atendimentos finalizados hoje`,
      ]
      if (bot != null && human != null && !Number.isNaN(bot) && !Number.isNaN(human)) {
        const diff = human - bot
        if (diff > 0.5) {
          lines.push(`O tempo médio de resolução do bot é cerca de ${Math.round(diff)} min menor que o dos atendentes humanos hoje`)
        } else if (diff < -0.5) {
          lines.push('O tempo médio de resolução do bot está acima do humano hoje — vale revisar fluxos do bot')
        } else {
          lines.push('Tempos médios de resolução do bot e do humano estão equilibrados hoje')
        }
      } else {
        lines.push('Acompanhe a evolução dos tempos conforme novos atendimentos forem finalizados')
      }
      return lines
    },
    narrativeRealtime() {
      const comInicio = this.hojeTotalInicioNoPeriodo
      const fin = this.hojeTotalResolvido
      const rate = this.hojeTaxaResolucaoHumano
      if (comInicio === 0 && fin === 0) {
        return 'Hoje ainda não há atendimentos com início nem finalizações carregadas para leitura agregada. Os números em tempo real acima refletem o momento atual da central (calendário e horários em horário de Brasília).'
      }
      const trechoFin = fin
        ? ` No recorte de finalizados carregado para hoje, ${rate}% foram por atendente humano.`
        : ''
      return `Hoje, ${comInicio.toLocaleString('pt-BR')} atendimento(s) tiveram início.${trechoFin} A visão acima mostra o pulso operacional de agora.`
    },
    periodTriggerLabel() {
      if (this.periodPreset === 'today') return 'Hoje'
      if (this.periodPreset === '7') return 'Últimos 7 dias'
      if (this.periodPreset === '30') return 'Últimos 30 dias'
      return 'Período personalizado'
    },
    periodRangeText() {
      const fmtIso = iso => {
        if (!iso) return '—'
        const p = iso.split('-').map(Number)
        if (p.length !== 3 || p.some(Number.isNaN)) return '—'
        const d = new Date(p[0], p[1] - 1, p[2])
        return d.toLocaleDateString('pt-BR', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      }
      if (!this.periodoInicioISOBr || !this.periodoFimISOBr) return '—'
      return `${fmtIso(this.periodoInicioISOBr)} – ${fmtIso(this.periodoFimISOBr)}`
    },
    /** Resolvidos com data de encerramento (civil BR) dentro do período; deduplicado por atendimento_id. */
    resolvidosPeriodoFimBr() {
      if (!this.periodoInicioISOBr || !this.periodoFimISOBr) return []
      return filterResolvidosFimNaFaixaBr(
        this.resolvidosPeriodo || [],
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    resolvidosPeriodoAnteriorFimBr() {
      const { prevInicio, prevFim } = this.periodoPrevBounds
      if (!prevInicio || !prevFim) return []
      return filterResolvidosFimNaFaixaBr(
        this.resolvidosPeriodoAnterior || [],
        prevInicio,
        prevFim,
      )
    },
    periodoEnriquecido() {
      return (this.resolvidosPeriodoFimBr || []).map(a => this.enriquecer(a))
    },
    periodoAnteriorEnriquecido() {
      return (this.resolvidosPeriodoAnteriorFimBr || []).map(a => this.enriquecer(a))
    },
    periodoTotalResolvido() {
      return this.resolvidosPeriodoFimBr.length
    },
    /** Atendimentos com data de início (BR) dentro do período selecionado. */
    periodoTotalInicioNoPeriodo() {
      return countComInicioNoIntervalo(
        this.atendimentosAtuais,
        this.resolvidosPeriodo,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    periodoTaxaResolucao() {
      const list = this.resolvidosPeriodoFimBr
      if (!list.length) return 0
      const human = list.filter(resolvidoPorAtendenteHumano).length
      return Math.round((100 * human) / list.length)
    },
    periodoPctBot() {
      const list = this.resolvidosPeriodoFimBr
      if (!list.length) return 0
      const b = list.filter(resolvidoPorBot).length
      return Math.round((100 * b) / list.length)
    },
    // ── Métricas da seção "Desempenho do período" baseadas em resolvidosPeriodo ──
    resolvidosBase() {
      return this.resolvidosPeriodo || []
    },
    totalAtendimentos() {
      return this.resolvidosBase.length
    },
    totalFinalizados() {
      return this.resolvidosBase.filter(a => a.status === STATUS_RESOLVIDO).length
    },
    detalheTotalAtendimentos() {
      const base = this.resolvidosBase
      const iniciadosPorAtendentes = base.filter(
        a => a.atendimento_bot === false && a.fim_bot === null && a.atendente_ativo !== null,
      ).length
      const emAndamento = base.filter(
        a => a.status === STATUS_EM_ANDAMENTO || a.fim === null,
      ).length
      return [
        { label: 'Iniciados pela equipe', value: iniciadosPorAtendentes },
        { label: 'ainda em andamento', value: emAndamento },
      ]
    },
    detalheTotalFinalizados() {
      const base = this.resolvidosBase
      const desconhecidos = base.filter(
        a => a.atendimento_bot === false && a.atendente_ativo === null && a.status === STATUS_RESOLVIDO,
      ).length
      const iniciadosPorAtendentes = base.filter(
        a => a.atendimento_bot === false && a.fim_bot === null && a.status === STATUS_RESOLVIDO && a.atendente_ativo !== null,
      ).length
      return [
        { label: 'Finalizados sem atendente', value: desconhecidos },
        { label: 'Iniciados pela equipe que foram finalizados', value: iniciadosPorAtendentes },
      ]
    },
    finalizadosPorHumanoBase() {
      return this.resolvidosBase.filter(
        a => a.atendimento_bot === false && a.atendente_ativo !== null && a.status === STATUS_RESOLVIDO,
      ).length
    },
    finalizadosPeloBotBase() {
      return this.resolvidosBase.filter(
        a => a.atendimento_bot === true && a.atendente_ativo === null && a.status === STATUS_RESOLVIDO,
      ).length
    },
    percentualHumanoBase() {
      if (!this.totalFinalizados) return 0
      return Math.round((this.finalizadosPorHumanoBase / this.totalFinalizados) * 100)
    },
    percentualBotBase() {
      if (!this.totalFinalizados) return 0
      return Math.round((this.finalizadosPeloBotBase / this.totalFinalizados) * 100)
    },
    tempoMedioResolucaoBase() {
      const vals = this.resolvidosBase
        .filter(a => a.status === STATUS_RESOLVIDO && a.inicio && a.fim)
        .map(a => tempoResolucaoMinutos(a))
        .filter(v => v != null && !Number.isNaN(v) && v >= 0)
      return average(vals)
    },
    periodoPrevBounds() {
      const span = Math.max(1, daysBetweenISOBr(this.periodoInicioISOBr, this.periodoFimISOBr))
      const prevFim = addDaysBrazilISO(this.periodoInicioISOBr, -1)
      const prevInicio = addDaysBrazilISO(this.periodoInicioISOBr, -span)
      return { prevInicio, prevFim, span }
    },
    /** Iniciadas por atendente: início civil BR no período (lista bruta da API para varrer início). */
    periodoIniciadasPorAtendentes() {
      return countIniciadasPorAtendenteNoIntervalo(
        [],
        this.resolvidosPeriodo,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    periodoFinalizadosHumano() {
      return contagemFinalizadosHumanoNaFaixaFimBr(
        this.resolvidosPeriodo,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    periodoFinalizadosBot() {
      return contagemFinalizadosBotNaFaixaFimBr(
        this.resolvidosPeriodo,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    /** Abertos agora (tempo real) cujo início civil BR cai no período selecionado. */
    periodoEmAndamentoInicioNoPeriodo() {
      return countAbertosComInicioNoIntervalo(
        this.atendimentosAtuais,
        this.periodoInicioISOBr,
        this.periodoFimISOBr,
      )
    },
    periodCards() {
      const prevRaw = this.resolvidosPeriodoAnterior || []

      const fmtDelta = (v, invert = false) => {
        if (v == null || Number.isNaN(v)) return null
        const good = invert ? v < 0 : v > 0
        const sym = v > 0 ? '+' : ''
        return { text: `${sym}${v}% vs período anterior`, positive: good }
      }

      const fmtDeltaPts = (v, invert = false) => {
        if (v == null || Number.isNaN(v)) return null
        const good = invert ? v < 0 : v > 0
        const sym = v > 0 ? '+' : ''
        return { text: `${sym}${v} p.p. vs anterior`, positive: good }
      }

      // Período anterior — mesmas métricas para comparação
      const prevTotalAtendimentos = prevRaw.length
      const prevTotalFinalizados = prevRaw.filter(a => a.status === STATUS_RESOLVIDO).length
      const prevFinHum = prevRaw.filter(
        a => a.atendimento_bot === false && a.atendente_ativo !== null && a.status === STATUS_RESOLVIDO,
      ).length
      const prevFinBot = prevRaw.filter(
        a => a.atendimento_bot === true && a.atendente_ativo === null && a.status === STATUS_RESOLVIDO,
      ).length
      const prevPctHum = prevTotalFinalizados ? Math.round((prevFinHum / prevTotalFinalizados) * 100) : null
      const prevPctBot = prevTotalFinalizados ? Math.round((prevFinBot / prevTotalFinalizados) * 100) : null
      const prevM2 = average(
        prevRaw
          .filter(a => a.status === STATUS_RESOLVIDO && a.inicio && a.fim)
          .map(a => tempoResolucaoMinutos(a))
          .filter(v => v != null && !Number.isNaN(v) && v >= 0),
      )

      const dTotal = fmtDelta(deltaPercentual(this.totalAtendimentos, prevTotalAtendimentos))
      const dTF = fmtDelta(deltaPercentual(this.totalFinalizados, prevTotalFinalizados))
      const dFH = fmtDelta(deltaPercentual(this.finalizadosPorHumanoBase, prevFinHum))
      const dFHPts = fmtDeltaPts(prevPctHum != null ? this.percentualHumanoBase - prevPctHum : null)
      const dFB = fmtDelta(deltaPercentual(this.finalizadosPeloBotBase, prevFinBot))
      const dFBPts = fmtDeltaPts(prevPctBot != null ? this.percentualBotBase - prevPctBot : null)
      const dM2 = fmtDelta(deltaPercentual(this.tempoMedioResolucaoBase, prevM2), true)

      const mergedDelta = (d1, d2) => {
        const parts = [d1, d2].filter(Boolean).map(d => d.text)
        if (!parts.length) return null
        const positive = [d1, d2].filter(Boolean).every(d => d.positive)
        return { text: parts.join(' · '), positive }
      }

      const dHumFinal = mergedDelta(dFH, dFHPts)
      const dBotFinal = mergedDelta(dFB, dFBPts)

      const pctHumLabel = this.totalFinalizados
        ? `${this.percentualHumanoBase}% dos finalizados`
        : `${this.percentualHumanoBase}%`
      const pctBotLabel = this.totalFinalizados
        ? `${this.percentualBotBase}% dos finalizados`
        : `${this.percentualBotBase}%`

      return [
        {
          key: 't',
          value: this.totalAtendimentos.toLocaleString('pt-BR'),
          label: 'Total de atendimentos',
          clickable: true,
          details: this.detalheTotalAtendimentos,
          delta: dTotal ? dTotal.text : null,
          deltaPositive: dTotal ? dTotal.positive : true,
        },
        {
          key: 'tf',
          value: this.totalFinalizados.toLocaleString('pt-BR'),
          label: 'Total finalizados',
          clickable: true,
          details: this.detalheTotalFinalizados,
          delta: dTF ? dTF.text : null,
          deltaPositive: dTF ? dTF.positive : true,
        },
        {
          key: 'fh',
          value: this.finalizadosPorHumanoBase.toLocaleString('pt-BR'),
          detail: pctHumLabel,
          label: 'Foram resolvidos pela equipe',
          delta: dHumFinal ? dHumFinal.text : null,
          deltaPositive: dHumFinal ? dHumFinal.positive : true,
        },
        {
          key: 'fb',
          value: this.finalizadosPeloBotBase.toLocaleString('pt-BR'),
          detail: pctBotLabel,
          label: 'Foram resolvidos pelo assistente',
          delta: dBotFinal ? dBotFinal.text : null,
          deltaPositive: dBotFinal ? dBotFinal.positive : true,
        },
        {
          key: 'x',
          value: formatMinutosLegivel(this.tempoMedioResolucaoBase),
          label: 'Tempo médio de resolução',
          delta: dM2 ? dM2.text : null,
          deltaPositive: dM2 ? dM2.positive : true,
        },
      ]
    },
    compareText() {
      if (!this.resolvidosPeriodoAnterior.length && this.periodPreset !== 'today') {
        return 'Sem dados do período anterior para comparação.'
      }
      if (!this.resolvidosPeriodoAnterior.length) return ''
      const d = daysBetweenISOBr(this.periodoInicioISOBr, this.periodoFimISOBr)
      return `Comparativo com o bloco anterior de ${d} dia(s) (calendário Brasil), encerrado antes do início deste período.`
    },
    narrativePeriod() {
      const { totalAtendimentos } = this
      const { totalFinalizados } = this
      const percentualHumano = this.percentualHumanoBase
      const percentualBot = this.percentualBotBase
      const desconhecidos = 100 - (percentualHumano + percentualBot)
      const base = this.resolvidosBase
      const iniciadosPorAtendentes = base.filter(
        a => a.atendimento_bot === false && a.fim_bot === null && a.status === STATUS_RESOLVIDO && a.atendente_ativo !== null,
      ).length
      if (!totalAtendimentos) {
        return 'Não há atendimentos registrados no período selecionado. Ajuste as datas ou aguarde novos atendimentos.'
      }
      return `No período selecionado, ${totalAtendimentos.toLocaleString('pt-BR')} atendimentos foram registrados. 
        Desses, ${totalFinalizados.toLocaleString('pt-BR')} foram finalizados, sendo ${percentualBot}% totalmente resolvidos pelo assistente e ${percentualHumano}% por atendentes. 
        Sendo que, ${iniciadosPorAtendentes.toLocaleString('pt-BR')} atendimentos foram iniciados diretamente pela equipe.
        Além disso, ${desconhecidos.toLocaleString('pt-BR')}% dos atendimentos foram finalizados sem atendente ou assistente vinculado.`
    },
    chartCategories() {
      return Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}h`)
    },
    chartTotal() {
      const s = seriePorHoraInicio(this.resolvidosPeriodoFimBr)
      return s.map(x => x.total)
    },
    chartHuman() {
      const s = seriePorHoraInicio(this.resolvidosPeriodoFimBr)
      return s.map(x => x.human)
    },
    chartBot() {
      const s = seriePorHoraInicio(this.resolvidosPeriodoFimBr)
      return s.map(x => x.bot)
    },
    bottleneckHourData() {
      return seriePorHoraInicio(this.resolvidosPeriodoFimBr)
    },
    bottleneckSummary() {
      const { label, busiestHour } = faixaCriticaPorPicos(this.bottleneckHourData)
      const unattended = this.atendimentosAtuais.filter(at => {
        if (at.atendimento_bot) return false
        return !(at.atendente_ativo && at.atendente_ativo.id)
      }).length
      const filaAlta = unattended >= 12
      const humanRes = this.avgResolucaoHumanoPeriodo
      const humanSlow = humanRes != null && humanRes > 25

      return [
        {
          icon: 'ClockIcon',
          title: 'Horário mais cheio',
          text: busiestHour != null
            ? `Pico em torno das ${String(busiestHour).padStart(2, '0')}h (início dos atendimentos no período).`
            : 'Sem picos claros no período analisado.',
        },
        {
          icon: 'ActivityIcon',
          title: 'Faixa crítica',
          text: label !== '—'
            ? `Concentração relevante entre ${label}.`
            : 'Distribuição relativamente uniforme ao longo do dia.',
        },
        {
          icon: 'AlertCircleIcon',
          title: 'Fila sem atendimento',
          text: filaAlta
            ? `Há ${unattended} conversas aguardando atendente humano agora — priorize distribuição ou mensagens automáticas.`
            : `Fila atual em ${unattended} conversa(s); dentro de um patamar operacional comum.`,
        },
        {
          icon: 'TrendingUpIcon',
          title: 'Resolução humana',
          text: humanSlow
            ? 'Tempo médio de resolução com atendente humano está alto no período; avalie carga e priorização.'
            : 'Tempo médio de resolução humano dentro de um patamar típico no período selecionado.',
        },
      ]
    },
    avgResolucaoHumanoPeriodo() {
      const vals = this.resolvidosPeriodoFimBr
        .filter(at => !at.atendimento_bot)
        .map(tempoResolucaoMinutos)
        .filter(v => v != null && !Number.isNaN(v))
      return average(vals)
    },
    listaHistoricoUnificada() {
      const openIds = new Set(this.atendimentosAtuais.map(at => at.atendimento_id))
      const itens = []
      this.atendimentosAtuais.forEach(at => {
        const clienteNome = at.cliente?.name || '—'
        const whatsapp = at.cliente?.whatsapp || at.from || '—'
        const atendenteId = at.atendente_ativo?.id
        const atendenteNome = this.mapaAtendentes[atendenteId] || null
        let statusConversa = STATUS_EM_ANDAMENTO
        if (at.atendimento_bot) {
          statusConversa = STATUS_EM_ANDAMENTO
        } else if (!atendenteId) {
          statusConversa = STATUS_NAO_ATENDIDA
        }
        itens.push({
          atendimento_id: at.atendimento_id,
          cliente: clienteNome,
          clienteNome,
          whatsapp,
          clienteWhatsapp: whatsapp,
          atendente_id: atendenteId,
          atendenteNome,
          atendimento_bot: at.atendimento_bot || false,
          statusConversa,
          inicio: at.inicio || at.created_at,
          fim: null,
        })
      })
      const resolvidosHistorico = this.resolvidosPeriodoFimBr || []
      resolvidosHistorico.forEach(at => {
        if (openIds.has(at.atendimento_id)) return
        const fim = at.fim || at.data_resolucao || at.ultima_mensagem_em
        if (!fim) return
        const clienteNome = at.cliente?.name || '—'
        const whatsapp = at.cliente?.whatsapp || at.from || '—'
        const atendenteId = at.atendente_ativo?.id
        const atendenteNome = this.mapaAtendentes[atendenteId] || null
        itens.push({
          atendimento_id: at.atendimento_id,
          cliente: clienteNome,
          clienteNome,
          whatsapp,
          clienteWhatsapp: whatsapp,
          atendente_id: atendenteId,
          atendenteNome,
          atendimento_bot: at.atendimento_bot || false,
          statusConversa: STATUS_RESOLVIDO,
          inicio: at.inicio || at.created_at,
          fim,
        })
      })
      return itens.sort((a, b) => {
        const ta = new Date(a.inicio || 0).getTime()
        const tb = new Date(b.inicio || 0).getTime()
        return tb - ta
      })
    },
    listaHistoricoFiltrada() {
      let lista = this.listaHistoricoUnificada
      if (this.filtroHistorico.cliente && this.filtroHistorico.cliente.trim()) {
        const termo = this.filtroHistorico.cliente.trim().toLowerCase()
        lista = lista.filter(item => {
          const nome = (item.cliente || item.clienteNome || '').toLowerCase()
          const wa = (item.whatsapp || item.clienteWhatsapp || '').toLowerCase()
          return nome.includes(termo) || wa.includes(termo)
        })
      }
      if (this.filtroHistorico.status) {
        lista = lista.filter(item => item.statusConversa === this.filtroHistorico.status)
      }
      if (this.filtroHistorico.atendente === 'bot') {
        lista = lista.filter(item => item.atendimento_bot === true)
      } else if (this.filtroHistorico.atendente) {
        lista = lista.filter(item => String(item.atendente_id) === this.filtroHistorico.atendente)
      }
      return lista
    },
    listaHistoricoPagina() {
      const start = (this.paginaHistorico - 1) * this.porPaginaHistorico
      return this.listaHistoricoFiltrada.slice(start, start + this.porPaginaHistorico)
    },
    opcoesStatusHistorico() {
      return [
        { value: null, text: 'Todos' },
        { value: STATUS_NAO_ATENDIDA, text: 'Não atendidas' },
        { value: STATUS_EM_ANDAMENTO, text: 'Em andamento' },
        { value: STATUS_RESOLVIDO, text: 'Resolvidos' },
      ]
    },
    opcoesAtendenteHistorico() {
      const list = [
        { value: null, text: 'Todos' },
        { value: 'bot', text: 'Bot/IA' },
      ]
      if (this.mapaAtendentes) {
        Object.keys(this.mapaAtendentes).forEach(id => {
          list.push({ value: String(id), text: this.mapaAtendentes[id] })
        })
      }
      return list
    },
  },
  watch: {
    chatSelecionado() {
      this.carregarTempoReal()
      this.carregarPeriodo()
    },
    filtroHistorico: {
      deep: true,
      handler() {
        this.paginaHistorico = 1
      },
    },
    resolvidosPeriodo() {
      this.paginaHistorico = 1
    },
  },
  mounted() {
    this.bootstrap()
    this.intervaloRefresh = setInterval(() => {
      this.carregarTempoReal()
    }, 60000)
  },
  beforeDestroy() {
    if (this.intervaloRefresh) {
      clearInterval(this.intervaloRefresh)
      this.intervaloRefresh = null
    }
  },
  methods: {
    enriquecer(a) {
      return {
        ...a,
        tempoResolucaoMin: tempoResolucaoMinutos(a),
      }
    },
    labelStatusHistorico(status) {
      const map = {
        [STATUS_NAO_ATENDIDA]: 'Não atendida',
        [STATUS_EM_ANDAMENTO]: 'Em andamento',
        [STATUS_RESOLVIDO]: 'Resolvido',
      }
      return map[status] || status
    },
    badgeStatusHistorico(status) {
      const map = {
        [STATUS_NAO_ATENDIDA]: 'warning',
        [STATUS_EM_ANDAMENTO]: 'info',
        [STATUS_RESOLVIDO]: 'success',
      }
      return map[status] || 'secondary'
    },
    formatarDataHoraHistorico(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        timeZone: TIMEZONE_BR,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    limparFiltrosHistorico() {
      this.filtroHistorico = { status: null, atendente: null, cliente: '' }
      this.paginaHistorico = 1
    },
    async abrirHistoricoAtendimento(item) {
      if (!item || !item.atendimento_id) return
      const emAndamento = item.statusConversa === STATUS_EM_ANDAMENTO
      this.historicoAtendimentoSelecionado = {
        mensagens: [],
        emAndamento,
        historicoAtendentes: [],
      }
      this.modalHistoricoVisivel = true
      try {
        const dados = await getChatOfContact(item.atendimento_id)
        this.historicoAtendimentoSelecionado = {
          mensagens: dados.mensagens || dados || [],
          emAndamento,
          historicoAtendentes: dados.historico_atendentes || [],
          status: emAndamento ? 'em_andamento' : (dados.status || 'resolvido'),
          fim: dados.fim || null,
        }
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Não foi possível carregar o histórico.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.historicoAtendimentoSelecionado = { mensagens: [], emAndamento: false, historicoAtendentes: [] }
      }
    },
    async bootstrap() {
      await this.facebookCredentials()
      await Promise.all([this.carregarTempoReal(), this.carregarPeriodo()])
    },
    async facebookCredentials() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []
      if ((contas.chats || []).length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if ((contas.chats || []).length > 1) {
        this.chatSelecionado = (contas.chats[0] && contas.chats[0].chat_token) || null
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
    onStoreChange() {
      this.carregarTempoReal()
      this.carregarPeriodo()
    },
    setPeriodPreset(p) {
      this.periodPreset = p
      const hoje = brazilTodayISO()
      if (p === 'today') {
        this.periodoInicioISOBr = hoje
        this.periodoFimISOBr = hoje
        this.carregarPeriodo()
      } else if (p === '7') {
        this.periodoFimISOBr = hoje
        this.periodoInicioISOBr = addDaysBrazilISO(hoje, -6)
        this.carregarPeriodo()
      } else if (p === '30') {
        this.periodoFimISOBr = hoje
        this.periodoInicioISOBr = addDaysBrazilISO(hoje, -29)
        this.carregarPeriodo()
      }
    },
    aplicarPeriodoCustom(payload) {
      this.periodPreset = 'custom'
      if (payload && payload.inicio && payload.fim) {
        this.periodoInicioISOBr = isoDatePickerToBrazilISO(payload.inicio)
        this.periodoFimISOBr = isoDatePickerToBrazilISO(payload.fim)
      }
      this.carregarPeriodo()
    },
    async carregarTempoReal() {
      if (!this.chatSelecionado) return
      this.carregando = true
      const uuid = this.chatSelecionado
      const hoje = brazilTodayISO()
      try {
        const [listaAtendentesAPI, dadosAtuais, dadosHoje] = await Promise.all([
          buscaAtendentes(uuid),
          getAttendings(uuid, {
            pagina: 0,
            limite: 5000,
            corteAntesDe: new Date().toISOString(),
          }),
          buscaResolvidos(uuid, hoje, hoje, 'all'),
        ])
        this.mapaAtendentes = {}
        const atendentesLista = listaAtendentesAPI.atendentes || []
        atendentesLista.forEach(a => {
          this.mapaAtendentes[a.id] = a.name
        })
        const listaAtual = Array.isArray(dadosAtuais) ? dadosAtuais : (dadosAtuais?.atendimentos || [])
        this.atendimentosAtuais = listaAtual.filter(at => at && at.atendimento_id)
        this.resolvidosHoje = (dadosHoje?.resolvidos || [])
      } catch (err) {
        console.error(err)
        this.$bvToast.toast(err.message || 'Erro ao carregar tempo real.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },
    async carregarPeriodo() {
      if (!this.chatSelecionado) return
      const inicioISO = this.periodoInicioISOBr
      const fimISO = this.periodoFimISOBr
      if (!inicioISO || !fimISO || inicioISO > fimISO) {
        this.$bvToast.toast('A data de início não pode ser maior que a data de fim.', {
          variant: 'warning',
          solid: true,
        })
        return
      }
      const tIni = new Date(`${inicioISO}T12:00:00-03:00`).getTime()
      const tFim = new Date(`${fimISO}T12:00:00-03:00`).getTime()
      const umMesEmMs = 1000 * 60 * 60 * 24 * 31
      if ((tFim - tIni) > umMesEmMs) {
        this.$bvToast.toast('O período não pode exceder 31 dias.', {
          title: 'Período muito longo',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.carregandoPeriodo = true
      const uuid = this.chatSelecionado
      const spanDays = Math.max(1, daysBetweenISOBr(inicioISO, fimISO))
      const prevFimISO = addDaysBrazilISO(inicioISO, -1)
      const prevInicioISO = addDaysBrazilISO(inicioISO, -spanDays)

      try {
        const [atual, anterior] = await Promise.all([
          buscaResolvidos(uuid, inicioISO, fimISO, 'all'),
          buscaResolvidos(uuid, prevInicioISO, prevFimISO, 'all'),
        ])
        this.resolvidosPeriodo = atual?.resolvidos || []
        this.resolvidosPeriodoAnterior = anterior?.resolvidos || []
      } catch (err) {
        console.error(err)
        this.$bvToast.toast(err.message || 'Erro ao carregar período.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoPeriodo = false
      }
    },
  },
}
</script>

<style>
body:has(.operacao-central-page) .content-area-wrapper {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

body:has(.operacao-central-page) .app-content.content {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

body:has(.operacao-central-page) .content-wrapper {
  padding: 0 !important;
  margin: 0 !important;
}

body:has(.operacao-central-page) .content-body {
  padding: 0 !important;
  margin: 0 !important;
}

body:has(.operacao-central-page) .content-right {
  width: 100% !important;
}
</style>

<style scoped>
.operacao-central-page {
  padding: 1.25rem 1.5rem 2.5rem;
  min-height: 100vh;
  max-width: 1280px;
  margin: 0 auto;
}

.co-store-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.05);
}

.co-store-select {
  max-width: 320px;
}

.co-historico.co-surface {
  background: #faf8fc;
  border-radius: 24px;
  padding: 1.75rem 1.75rem 1.5rem;
  border: 1px solid rgba(106, 27, 154, 0.08);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
}

.co-historico__title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #4a148c;
  letter-spacing: -0.02em;
}

.co-historico__desc {
  font-size: 0.9rem;
  color: #616161;
  max-width: 720px;
  line-height: 1.5;
}

.co-historico__badge {
  font-weight: 600;
}

.co-historico__filtros {
  border: none;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.co-historico__table >>> tbody tr {
  cursor: pointer;
}
</style>
